import type { GameEvent } from '../schema';

export const FINANCE_EVENTS: GameEvent[] = [
  {
    id: 'finance.firstSurplus',
    title: 'There is money left over',
    description: 'For the first time, the month ended with something in the account. What happens to it matters more than the amount.',
    category: 'finance',
    pools: ['pool.finance.early'],
    weight: 8,
    unique: true,
    requires: [{ k: 'num', path: 'cashFlow', op: '>', value: 1500 }],
    choices: [
      {
        id: 'save',
        label: 'Build a buffer',
        hint: 'safe',
        tags: ['security', 'discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Started saving',
            narrative: 'Unexciting, and the single highest-return habit available to you.',
            tone: 'positive',
            effects: [
              { k: 'habit', habit: 'saving', delta: 14 },
              { k: 'aspiration', op: 'adopt', defId: 'goal.emergencyFund' },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'invest',
        label: 'Start investing it',
        hint: 'mixed',
        tags: ['finance', 'patience'],
        outcomes: [
          {
            weight: 1,
            label: 'Started investing early',
            narrative: 'Small amounts, thirty years of runway. The maths on this is almost unfair.',
            tone: 'positive',
            effects: [
              { k: 'habit', habit: 'investing', delta: 16 },
              { k: 'aspiration', op: 'adopt', defId: 'goal.investingHabit' },
              { k: 'invest', op: 'buy', assetId: 'balanced', amount: 2000 },
            ],
          },
        ],
      },
      {
        id: 'spend',
        label: 'Improve your life a bit',
        hint: 'safe',
        tags: ['lifestyle'],
        outcomes: [
          {
            weight: 1,
            label: 'Levelled up',
            narrative: 'A better flat, better food, better shoes. Genuinely nicer, and now permanent.',
            tone: 'neutral',
            effects: [
              { k: 'lifestyle', delta: 1 },
              { k: 'stat', stat: 'happiness', delta: 7 },
              { k: 'habit', habit: 'spending', delta: 10 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.lifestyleCreep',
    title: 'The raise has already gone',
    description: 'You earn noticeably more than two years ago and save exactly the same amount.',
    category: 'finance',
    pools: ['pool.finance.early', 'pool.finance.mid'],
    weight: 6,
    cooldown: 6,
    requires: [{ k: 'num', path: 'income', op: '>=', value: 55000 }, { k: 'habit', habit: 'spending', op: '>=', value: 45 }],
    choices: [
      {
        id: 'cut',
        label: 'Cut back deliberately',
        hint: 'costly',
        tags: ['discipline', 'security'],
        outcomes: [
          {
            weight: 1,
            label: 'Downshifted',
            narrative: 'You gave up two things you barely noticed and redirected the difference.',
            tone: 'positive',
            effects: [
              { k: 'lifestyle', delta: -1 },
              { k: 'habit', habit: 'saving', delta: 10 },
              { k: 'habit', habit: 'spending', delta: -10 },
              { k: 'stat', stat: 'happiness', delta: -3 },
            ],
          },
        ],
      },
      {
        id: 'automate',
        label: 'Automate the saving instead',
        detail: 'Same life, money moves before you see it.',
        hint: 'safe',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Automated it',
            narrative: 'The only reliable trick: make the good decision once, then stop deciding.',
            tone: 'positive',
            effects: [
              { k: 'habit', habit: 'saving', delta: 16 },
              { k: 'habit', habit: 'investing', delta: 8 },
              { k: 'stat', stat: 'stress', delta: -3 },
            ],
          },
        ],
      },
      {
        id: 'ignore',
        label: 'It is fine',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Carried on',
            narrative: 'It was fine. It stayed fine until it was not.',
            tone: 'neutral',
            effects: [
              { k: 'habit', habit: 'spending', delta: 8 },
              { k: 'lifestyle', delta: 1 },
              { k: 'stat', stat: 'happiness', delta: 4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.debtSpiral',
    title: 'The interest is winning',
    description: 'The minimum payments are affordable and the balance is not moving. This is by design — just not yours.',
    category: 'finance',
    pools: ['pool.finance.early', 'pool.finance.mid', 'pool.crisis'],
    weight: 7,
    cooldown: 4,
    requires: [{ k: 'num', path: 'debtPressure', op: '>=', value: 48 }],
    choices: [
      {
        id: 'attack',
        label: 'Attack the highest rate',
        detail: 'Cut everything else. Pay it down aggressively.',
        hint: 'costly',
        tags: ['discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Paid it down',
            narrative: 'Two grim years of no holidays and a spreadsheet you checked weekly.',
            tone: 'positive',
            effects: [
              { k: 'lifestyle', delta: -2 },
              { k: 'debt', op: 'pay', amount: 18000 },
              { k: 'habit', habit: 'saving', delta: 10 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'stat', stat: 'stress', delta: -8 },
              { k: 'aspiration', op: 'adopt', defId: 'goal.debtFree' },
            ],
          },
        ],
      },
      {
        id: 'consolidate',
        label: 'Consolidate at a lower rate',
        hint: 'mixed',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 62,
            label: 'Approved',
            narrative: 'One payment, lower rate, longer term. Cheaper monthly, more expensive eventually.',
            tone: 'neutral',
            favouredBy: [{ conditions: [{ k: 'num', path: 'income', op: '>=', value: 60000 }], multiplier: 1.7 }],
            effects: [
              { k: 'debt', op: 'forgive', kind: 'consumer', fraction: 1 },
              { k: 'debt', op: 'add', kind: 'consumer', label: 'Consolidation loan', amount: 22000, rate: 0.082, years: 9 },
              { k: 'stat', stat: 'stress', delta: -9 },
            ],
          },
          {
            weight: 38,
            label: 'Declined',
            narrative: 'Your credit file said no. The rate you have is the rate you get.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'stress', delta: 9 },
              { k: 'stat', stat: 'happiness', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'minimum',
        label: 'Keep paying the minimum',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Treading water',
            narrative: 'Technically current. Functionally stuck.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'stress', delta: 8 },
              { k: 'inertia', op: 'pressure', delta: 6 },
              { k: 'schedule', afterYears: 3, label: 'Debt kept compounding', requires: [{ k: 'num', path: 'debtPressure', op: '>=', value: 45 }], effects: [{ k: 'stat', stat: 'stress', delta: 10 }, { k: 'stat', stat: 'health', delta: -4 }] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.homePurchase',
    title: 'A place of your own',
    description:
      'The deposit is just about there. A mortgage would fix your housing cost for decades and chain you to one city.',
    category: 'finance',
    pools: ['pool.finance.mid'],
    weight: 7,
    cooldown: 10,
    requires: [
      { k: 'num', path: 'cash', op: '>=', value: 34000 },
      { k: 'flag', flag: 'ownsHome', value: false },
      { k: 'age', min: 24 },
    ],
    weightMods: [
      { conditions: [{ k: 'marital', anyOf: ['married', 'partnered'] }], multiplier: 1.8 },
      { conditions: [{ k: 'aspiration', defId: 'dream.ownHome', status: 'active' }], multiplier: 2 },
    ],
    choices: [
      {
        id: 'buy',
        label: 'Buy',
        detail: 'Deposit down, mortgage signed, roots planted.',
        hint: 'mixed',
        tags: ['security', 'commitment'],
        outcomes: [
          {
            weight: 1,
            label: 'Bought a home',
            narrative: 'Keys, a mortgage statement, and the unfamiliar feeling of being somewhere on purpose.',
            tone: 'positive',
            effects: [
              { k: 'property', op: 'buy', value: 285000, deposit: 32000, rate: 0.049, years: 28 },
              { k: 'fixedExpenses', mode: 'add', value: 2400 },
              { k: 'stat', stat: 'happiness', delta: 10 },
              { k: 'stat', stat: 'stress', delta: 6 },
              { k: 'aspiration', op: 'resolve', defId: 'dream.ownHome', status: 'achieved' },
              { k: 'memory', title: 'Bought a home', tone: 'milestone', category: 'finance', weight: 9 },
            ],
          },
        ],
      },
      {
        id: 'rent',
        label: 'Keep renting and invest the deposit',
        hint: 'mixed',
        tags: ['freedom', 'finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed liquid',
            narrative: 'Flexible, diversified, and quietly judged at every family dinner.',
            tone: 'neutral',
            effects: [
              { k: 'invest', op: 'buy', assetId: 'growth', amount: 30000 },
              { k: 'habit', habit: 'investing', delta: 8 },
              { k: 'trait', trait: 'adaptability', delta: 3 },
            ],
          },
        ],
      },
      {
        id: 'wait',
        label: 'Wait for a better moment',
        hint: 'safe',
        defers: { id: 'int.home', label: 'Buy a home', topic: 'finance', urgency: 20 },
        outcomes: [
          {
            weight: 1,
            label: 'Waited',
            narrative: 'Prices have historically rewarded waiting roughly never, but it felt prudent.',
            tone: 'neutral',
            effects: [{ k: 'inertia', op: 'pressure', delta: 4 }],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.unexpectedBill',
    title: 'An unavoidable expense',
    description: 'A boiler, a car, a tooth. Something essential has failed and the timing is irrelevant.',
    category: 'finance',
    pools: ['pool.finance.early', 'pool.finance.mid', 'pool.finance.late'],
    weight: 9,
    cooldown: 2,
    choices: [
      {
        id: 'pay',
        label: 'Pay it',
        hint: 'costly',
        outcomes: [
          {
            weight: 1,
            label: 'Dealt with it',
            narrative: 'This is precisely what the emergency fund is for. Or the credit card, if there is no fund.',
            tone: 'neutral',
            effects: [{ k: 'cashIncomeMonths', months: -1.1, note: 'emergency repair' }],
          },
        ],
      },
      {
        id: 'defer',
        label: 'Patch it and hope',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 48,
            label: 'It held',
            narrative: 'The cheap fix held. You got away with it.',
            tone: 'positive',
            effects: [{ k: 'cashIncomeMonths', months: -0.25, note: 'temporary fix' }],
          },
          {
            weight: 52,
            label: 'It failed worse',
            narrative: 'It failed again, comprehensively, and now costs double.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -2.2, note: 'failed repair' },
              { k: 'stat', stat: 'stress', delta: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.windfall',
    title: 'Money you did not earn',
    description: 'An inheritance, a settlement, a bonus far beyond the band. It is real, and it is one-off.',
    category: 'luck',
    pools: ['pool.finance.mid', 'pool.luck'],
    weight: 3,
    cooldown: 14,
    choices: [
      {
        id: 'invest',
        label: 'Invest all of it',
        hint: 'safe',
        tags: ['finance', 'patience'],
        outcomes: [
          {
            weight: 1,
            label: 'Invested the lot',
            narrative: 'You never saw it in your current account, which is exactly why it survived.',
            tone: 'positive',
            effects: [
              { k: 'cash', amount: 42000 },
              { k: 'invest', op: 'buy', assetId: 'balanced', amount: 42000 },
              { k: 'habit', habit: 'investing', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'split',
        label: 'Split it — some now, most later',
        hint: 'safe',
        tags: ['balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Split it',
            narrative: 'One good holiday, one boring index fund. A defensible allocation.',
            tone: 'positive',
            effects: [
              { k: 'cash', amount: 42000 },
              { k: 'invest', op: 'buy', assetId: 'balanced', amount: 30000 },
              { k: 'stat', stat: 'happiness', delta: 9 },
              { k: 'counter', name: 'travelTrips', delta: 1 },
              { k: 'relationshipAll', dim: 'closeness', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'spend',
        label: 'Actually enjoy it',
        hint: 'mixed',
        tags: ['lifestyle'],
        outcomes: [
          {
            weight: 1,
            label: 'Spent it',
            narrative: 'A car, a kitchen, a year of feeling wealthy. You will remember it fondly and inaccurately.',
            tone: 'neutral',
            effects: [
              { k: 'cash', amount: 42000 },
              { k: 'lifestyle', delta: 2 },
              { k: 'stat', stat: 'happiness', delta: 15 },
              { k: 'habit', habit: 'spending', delta: 12 },
              { k: 'cash', amount: -34000, note: 'windfall spending' },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.familyAsk',
    title: 'Family needs money',
    description: 'Someone you love is short, and asking you is clearly humiliating for them.',
    category: 'finance',
    pools: ['pool.finance.mid', 'pool.family'],
    weight: 5,
    cooldown: 5,
    requires: [{ k: 'num', path: 'cash', op: '>=', value: 6000 }],
    choices: [
      {
        id: 'give',
        label: 'Give it, no strings',
        hint: 'costly',
        tags: ['family', 'generosity'],
        outcomes: [
          {
            weight: 1,
            label: 'Helped',
            narrative: 'You called it a gift so nobody would have to pretend it was a loan.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -1.5, note: 'family support' },
              { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: 12 },
              { k: 'relationshipAll', kind: 'sibling', dim: 'trust', delta: 10 },
              { k: 'stat', stat: 'purpose', delta: 5 },
              { k: 'counter', name: 'contributions', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'lend',
        label: 'Lend it',
        hint: 'mixed',
        tags: ['family'],
        outcomes: [
          {
            weight: 40,
            label: 'Repaid',
            narrative: 'They paid it back in full, slowly, and never mentioned it again.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', kind: 'sibling', dim: 'trust', delta: 8 },
              { k: 'stat', stat: 'happiness', delta: 3 },
            ],
          },
          {
            weight: 60,
            label: 'Never repaid',
            narrative: 'The money is gone and the relationship now has a shape it did not have before.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -1.5, note: 'unrepaid loan' },
              { k: 'relationshipAll', kind: 'sibling', dim: 'conflict', delta: 18 },
              { k: 'relationshipAll', kind: 'sibling', dim: 'trust', delta: -12 },
              { k: 'stat', stat: 'stress', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'refuse',
        label: 'Say no',
        hint: 'mixed',
        tags: ['boundaries'],
        outcomes: [
          {
            weight: 1,
            label: 'Refused',
            narrative: 'Financially correct. It sat badly for years.',
            tone: 'negative',
            effects: [
              { k: 'relationshipAll', kind: 'sibling', dim: 'closeness', delta: -16 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: -10 },
              { k: 'regret', op: 'add', id: 'regret.familyMoney', label: 'Refused family when they asked', weight: 12 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.insuranceOffer',
    title: 'Protection you hope to waste',
    description: 'Income protection and life cover. A cost every year, for a year you hope never arrives.',
    category: 'finance',
    pools: ['pool.finance.mid'],
    weight: 5,
    unique: true,
    requires: [{ k: 'num', path: 'income', op: '>=', value: 40000 }, { k: 'age', min: 26 }],
    weightMods: [{ conditions: [{ k: 'num', path: 'dependents', op: '>=', value: 1 }], multiplier: 2.2 }],
    choices: [
      {
        id: 'take',
        label: 'Take the cover',
        hint: 'safe',
        tags: ['security'],
        outcomes: [
          {
            weight: 1,
            label: 'Insured',
            narrative: 'A direct debit that buys you the ability to stop worrying about one specific catastrophe.',
            tone: 'positive',
            effects: [
              { k: 'flag', flag: 'insured', value: true },
              { k: 'fixedExpenses', mode: 'add', value: 1450 },
              { k: 'stat', stat: 'stress', delta: -6 },
            ],
          },
        ],
      },
      {
        id: 'skip',
        label: 'Self-insure',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Declined',
            narrative: 'Cheaper every year, right up until the year it is not.',
            tone: 'neutral',
            effects: [{ k: 'flag', flag: 'insured', value: false }],
          },
        ],
      },
    ],
  },
  {
    id: 'finance.retirementMath',
    title: 'Running the numbers',
    description: 'You finally sit down and calculate what retirement actually requires. The figure is educational.',
    category: 'finance',
    pools: ['pool.finance.late', 'pool.finance.retire'],
    weight: 7,
    cooldown: 8,
    requires: [{ k: 'age', min: 45 }],
    choices: [
      {
        id: 'boost',
        label: 'Increase contributions hard',
        hint: 'costly',
        tags: ['discipline', 'finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Redirected income',
            narrative: 'A visible cut to your standard of living in exchange for an invisible future.',
            tone: 'positive',
            effects: [
              { k: 'lifestyle', delta: -1 },
              { k: 'retirement', mode: 'add', value: 0 },
              { k: 'habit', habit: 'saving', delta: 14 },
              { k: 'counter', name: 'retirementBoost', delta: 1 },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'workLonger',
        label: 'Plan to work longer',
        hint: 'mixed',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Extended the plan',
            narrative: 'Five more years solves the arithmetic. It assumes five more good years.',
            tone: 'neutral',
            effects: [
              { k: 'flag', flag: 'planWorkLonger', value: true },
              { k: 'stat', stat: 'happiness', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'ignore',
        label: 'Close the spreadsheet',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Looked away',
            narrative: 'You will deal with it. Later. Definitely.',
            tone: 'negative',
            effects: [
              { k: 'inertia', op: 'pressure', delta: 8 },
              { k: 'regret', op: 'add', id: 'regret.retirement', label: 'Left retirement planning too late', weight: 14 },
            ],
          },
        ],
      },
    ],
  },
];
